import { getCategory } from "./utils";

const buildImageItems = (image) => {
  // Always preserve incoming image order (no category-based reordering)
  if (Array.isArray(image) && image.length > 0) {
    return image.map((item) => ({
      type: "image",
      data: item,
      category: getCategory(item?.image_category),
    }));
  }
  if (image && typeof image === "object") {
    // Backward compat: flatten any object-shaped image data without imposing an explicit ordering
    return Object.values(image)
      .flat()
      .filter(Boolean)
      .map((item) => ({
        type: "image",
        data: item,
        category: getCategory(item?.image_category),
      }));
  }
  return [];
};

export const transformMediaDataToItems = (skuData, isFirstSixExt = true, mobileView = false) => {
  const teamConfig = skuData?.team_config || {};
  const int360InConfig = teamConfig["360Int"]?.isActive;

  // A dealer can ask for the feature video to sit at a fixed 1-based slot
  // inside the image set itself (e.g. the 3rd or 5th item overall) instead
  // of occupying its own block. When that slot is configured, video is
  // spliced into the image run below and skipped in the regular block
  // ordering entirely.
  const videoImageSlot = Number(teamConfig["video"]?.imageSlot) || 0;
  const interleaveVideoInImages =
    videoImageSlot > 0 && !!teamConfig["video"]?.isActive && !!skuData?.video;

  let positionMap = {
    360: teamConfig["360"]?.position || 0,
    video: teamConfig["video"]?.position || 0,
    image: teamConfig["image"]?.position || 0,
    threed: teamConfig["threed"]?.position || 0,
  };

  if (int360InConfig) {
    positionMap["360Int"] = teamConfig["360Int"]?.position || 0;
  }

  let mediaTypes = Object.entries(positionMap)
    .filter(([type, position]) => {
      if (interleaveVideoInImages && type === "video") return false;
      if (!position) return false;
      if (type === "360") return teamConfig["360"]?.isActive && skuData?.["360"];
      if (type === "360Int") return teamConfig["360Int"]?.isActive && skuData?.["360Int"];
      return true;
    })
    .sort(([_, posA], [__, posB]) => posA - posB)
    .map(([type]) => type);

  // Legacy spin-only: interior not in team_config, append after sorted products
  if (!int360InConfig && skuData?.["360Int"]) {
    mediaTypes.push("360Int");
  }

  const items = [];
  for (const type of mediaTypes) {
    if (skuData[type]) {
      if (type === "image") {
        const imageItems = buildImageItems(skuData.image);
        if (interleaveVideoInImages) {
          const insertAt = Math.min(videoImageSlot - 1, imageItems.length);
          imageItems.splice(insertAt, 0, { type: "video", data: skuData.video });
        }
        items.push(...imageItems);
      } else if (type === "360Int") {
        items.push({
          type: "360Int",
          data: skuData["360Int"],
        });
      } else {
        items.push({
          type: type,
          data: skuData[type],
        });
      }
    }
  }

  // No images to interleave into (e.g. video arrived without a catalog) —
  // append the video rather than silently dropping it.
  if (interleaveVideoInImages && !items.some((item) => item.type === "video")) {
    items.push({ type: "video", data: skuData.video });
  }

  return items;
};
